/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ArrayExtensions, IDistributorInfo } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { OrgUnit } from '@msdyn365-commerce/retail-proxy';
import React from 'react';
import { observable, reaction } from 'mobx';
import classnames from 'classnames';
import { IDistributorSelectorResources } from '../distributor-selector.props.autogenerated';
import { DistributorSelectorLineItemComponent } from './distributor-selector-line-item.components';

/**
 * Distributor selector lines props interface.
 */
export interface IDistributorSelectorLinesProps {
    mainClassName: string;
    distributors?: IDistributorInfo[];
    resources: IDistributorSelectorResources;
    selectedDistributorId?: number;
    shouldDisplayList?: boolean;
    isLocationDisabled?: boolean;
    currentDistributorId: number | undefined;
    shouldShowIndex: boolean;
    distributorDetailsToBeShown?: string[];
    selectDistributorText?: string;
    /**
     * The telemetry content.
     */
    telemetryContent?: ITelemetryContent;
    onDistributorSelected(distributor: OrgUnit | undefined): void;
    onSetAsCurrentDistributor: (distributorId: number | undefined) => void;
    onViewAllDistributorDetails: (distributorId: number | undefined) => void;
}

export class DistributorSelectorLines extends React.PureComponent<IDistributorSelectorLinesProps> {
    private readonly selectedDistributorRef: React.RefObject<HTMLDivElement>;
    private distributorCounter: number;
    @observable private shouldDisplayList: boolean | undefined;

    public constructor(props: IDistributorSelectorLinesProps) {
        super(props);
        this.selectedDistributorRef = React.createRef();
        this.distributorCounter = 0;
        this.shouldDisplayList = this.props.shouldDisplayList;
    }

    public componentDidMount(): void {
        reaction(
            () => this.shouldDisplayList,
            () => {
                setTimeout(() => {
                    this._scrollIntoView();
                }, 0);
            }
        );
    }

    public componentDidUpdate(previousProps: IDistributorSelectorLinesProps): void {
        if (this.props.selectedDistributorId !== previousProps.selectedDistributorId) {
            this._scrollIntoView();
        }
    }

    public render(): JSX.Element | null {
        const { distributors, mainClassName } = this.props;
        this.shouldDisplayList = this.props.shouldDisplayList;

        if (!ArrayExtensions.hasElements(distributors)) {
            return null;
        }

        this.distributorCounter = 0;

        return (
            <div className={classnames(`${mainClassName}__distributors`)} role='list'>
                {distributors.map((distributor, index) => {
                    return this._renderDistributor(distributor, index);
                })}
            </div>
        );
    }

    /**
     * On click Handler function.
     * @param orgUnit -OrgUnit distributor.
     * @returns Click action on orgUnit Distributor.
     */
    private readonly onDistributorSelected = (orgUnit: OrgUnit | undefined) => () => {
        this.props.onDistributorSelected(orgUnit);
    };

    private _renderDistributor(distributor: IDistributorInfo, index: number): JSX.Element | undefined {
        const {
            distributors,
            mainClassName,
            currentDistributorId,
            resources,
            telemetryContent,
            distributorDetailsToBeShown,
            selectDistributorText,
            shouldShowIndex,
            onSetAsCurrentDistributor,
            onViewAllDistributorDetails
        } = this.props;
        const selectedDistributorId = this.props.selectedDistributorId;
        const defaultAriaSetsize = 0;
        const orgUnit = distributor?.OrgUnit;
        if (distributor) {
            ++this.distributorCounter;
            return (
                <div
                    className={classnames(`${mainClassName}__distributor`, {
                        selected: selectedDistributorId === orgUnit?.RecordId
                    })}
                    key={index}
                    role='listitem'
                    aria-setsize={ArrayExtensions.hasElements(distributors) ? distributors.length : defaultAriaSetsize}
                    aria-posinset={this.distributorCounter}
                    onClick={this.onDistributorSelected(orgUnit)}
                    ref={selectedDistributorId === orgUnit?.RecordId ? this.selectedDistributorRef : undefined}
                >
                    <DistributorSelectorLineItemComponent
                        mainClassName={mainClassName}
                        distributor={distributor}
                        resources={resources}
                        index={this.distributorCounter.toString()}
                        currentDistributorId={currentDistributorId}
                        telemetryContent={telemetryContent}
                        shouldShowIndex={shouldShowIndex}
                        distributorDetailsToBeShown={distributorDetailsToBeShown}
                        selectDistributorText={selectDistributorText}
                        handlers={{
                            onSetAsCurrentDistributor: onSetAsCurrentDistributor,
                            onViewAllDistributorDetails: onViewAllDistributorDetails
                        }}
                    />
                </div>
            );
        }
        return undefined;
    }

    /**
     * Scroll into view.
     */
    private readonly _scrollIntoView = () => {
        const selectedDistributorDiv = this.selectedDistributorRef.current;
        // In devices with a screen width of less than 768px, we display a toggle button that allows users to switch between the distributor list and the map.
        // The distributor list is initially shown in the list view, followed by the map. Each view has its own scrolling behavior, and they are contained within separate parent wrapper div's.
        const parent = this.props.shouldDisplayList
            ? selectedDistributorDiv?.parentElement?.parentElement
            : selectedDistributorDiv?.parentElement;
        if (selectedDistributorDiv && parent) {
            if (this.props.shouldDisplayList) {
                if (parent.scrollTop > selectedDistributorDiv.offsetTop) {
                    parent.scrollTop = selectedDistributorDiv.offsetTop - 10;
                } else if (
                    selectedDistributorDiv.offsetTop + selectedDistributorDiv.clientHeight >
                    parent.scrollTop + parent.clientHeight
                ) {
                    parent.scrollTop = selectedDistributorDiv.offsetTop + selectedDistributorDiv.clientHeight - parent.clientHeight - 10;
                }
            } else {
                if (parent.scrollLeft > selectedDistributorDiv.offsetLeft) {
                    parent.scrollLeft = selectedDistributorDiv.offsetLeft;
                } else if (
                    selectedDistributorDiv.offsetLeft + selectedDistributorDiv.clientHeight >
                    parent.scrollLeft + parent.clientHeight
                ) {
                    parent.scrollLeft = selectedDistributorDiv.offsetLeft + selectedDistributorDiv.clientHeight - parent.clientHeight - 10;
                }
            }
        }
    };
}
